import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { PrimaryButton } from 'components/widgets/buttons';
import { TextInput } from 'components/forms/form-elements';
import { alerts } from 'modules/alerts/actions';
import Person from 'services/data/person';
import PurchaseOrder from 'services/data/purchase-order';
import Tooltip from 'components/widgets/overlays/tooltip';
import replaceById from 'utils/replace-by-id';


export default class ApproveExpenseBill extends React.Component {
  static propTypes = {
    purchaseOrder: PropTypes.object,
    expenseBill: PropTypes.object,
    currentUser: PropTypes.object
  };

  constructor() {
    super();
    this.state = {
      clientApprover: '',
      blurred: false,
      approving: false,
      syncing: false
    };

    this.startApprovingExpenseBill = this.startApprovingExpenseBill.bind(this);
    this.submit = this.submit.bind(this);
    this.approveExpenseBill = this.approveExpenseBill.bind(this);
  }

  // If the purchase order now has approvedBy set, cancel the approving process
  // (this should only happen when the user has accidentally opened several approving forms at
  // a time and then sets client approver on the PO, at which point they should not need to enter
  // it again in order to approve other charges)
  componentWillReceiveProps(nextProps) {
    if (nextProps.purchaseOrder.approvedBy) {
      this.setState({ approving: false });
    }
  }

  // If the PO already has approvedBy set, just approve the charge.
  // Otherwise, require the user to input who approved the PO at the client's office
  startApprovingExpenseBill() {
    if (!_.isEmpty(this.props.purchaseOrder.approvedBy)) {
      this.approveExpenseBill(this.props.expenseBill);
    } else {
      this.setState({ approving: true });
    }
  }

  // Forms by default refresh the page, so we prevent the default and set blurred
  // so the required error shows up if necessary.
  // If the user has input the client approver, approve the expense bill and set the
  // client approver on the PO
  submit(e) {
    e.preventDefault();
    this.setState({ blurred: true });

    if (this.state.clientApprover.length) {
      this.approveExpenseBill(this.props.expenseBill, this.state.clientApprover);
    }
  }

  // Approve expense bill and refresh data
  approveExpenseBill(expenseBill, clientApprover) {
    this.setState({ syncing: true });

    const purchaseOrderToSave = {
      id: expenseBill.purchaseOrder,
      expenseBills: [{ id: expenseBill.id, approved: true, approvedBy: this.props.currentUser.id }],
      approvedBy: clientApprover
    };

    return PurchaseOrder.update(purchaseOrderToSave)
      .then((updatedPurchaseOrder) => {
        const purchaseOrder = _.cloneDeep(this.props.purchaseOrder);
        const expenseBillUpdate = _.find(updatedPurchaseOrder.expenseBills, { id: expenseBill.id });
        const updatedExpenseBill = _.assign({}, expenseBill, _.pick(expenseBillUpdate, ['approved', 'approvedBy']));
        
        purchaseOrder.approvedBy = updatedPurchaseOrder.approvedBy;
        purchaseOrder.unapprovedExpenseBills = updatedPurchaseOrder.unapprovedExpenseBills;
        purchaseOrder.expenseBills = replaceById(updatedExpenseBill, purchaseOrder.expenseBills);

        this.props.purchaseOrder.update(purchaseOrder);
        this.setState({ syncing: false });
      })
      .catch((err) => {
        this.setState({ syncing: false });
        alerts.catchError(err);
      });
  }


  render() {
    const { purchaseOrder, expenseBill, currentUser } = this.props;
    const lineItemTotals = _.map(expenseBill.lineItems, (lineItem) => (lineItem.quantity * lineItem.unitCost) || 0);
    let calculatedTotal = _.sum(lineItemTotals) + Number(expenseBill.shipping) + Number(expenseBill.tax);
    calculatedTotal = _.round(calculatedTotal, 2); // Round to nearest .01 to prevent floating point precision errors

    let disabledReason = '';
    if (currentUser.id !== expenseBill.createdBy && !Person.isLead(currentUser)) {
      disabledReason = 'You can\'t approve someone else\'s charge';
    } else if (_.isEmpty(purchaseOrder.ticket) || _.isEmpty(purchaseOrder.createdBy)) {
      disabledReason = 'Set ticket and purchaser on the PO before approving charges!';
    } else if (calculatedTotal !== Number(expenseBill.amount)) {
      disabledReason = 'Sum of line items doesn\'t match total. Edit charge to enter correct line item prices before approving.';
    }

    // If the user tried to approve the charge, but the PO doesn't have approvedBy set,
    // require the user to input approvedBy before allowing them to approve the charge
    if (this.state.approving) {
      return (
        <form onSubmit={this.submit}>
          <TextInput
            elementClasses="client-approver-element"
            placeholder="Client approver"
            onChange={(e) => this.setState({ clientApprover: e.target.value })}
            onBlur={() => this.setState({ blurred: true })}
            error={!this.state.clientApprover.length ? 'Required' : ''}
            touched={this.state.blurred}
            autoFocus
          />
          <PrimaryButton
            type="submit"
            showLoading={this.state.syncing}
            content="Save & Approve"
          />
        </form>
      );
    }

    // By default, show the Approve button, but disable it if necessary.
    return (
      <Tooltip title={disabledReason} className="inline-block">
        <PrimaryButton
          onClick={this.startApprovingExpenseBill}
          showLoading={this.state.syncing}
          disabled={disabledReason}
        >
          <span>Approve</span>
        </PrimaryButton>
      </Tooltip>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/models/purchases/approve-expense-bill.js